import React from 'react'
import classNames from 'classnames'
import {
  ImageResolutionsProps,
  RenderImgSourceProps
} from './ImageResolutions.d'
import { Card } from '@toasttab/shared/src/utils/craftUtils'

const getResponsiveWidths = (minWidth: string | null) => {
  if (!minWidth) return { w1x: 375, w2x: 750 }
  const width = parseInt(minWidth)
  return {
    w1x: width,
    w2x: width * 2
  }
}

const buildSrcSet = (
  url: string,
  minWidth: string | null,
  format: 'avif' | null = null
) => {
  if (!url) return ''
  const baseUrl = url.split('?')[0]
  const { w1x, w2x } = getResponsiveWidths(minWidth)
  return `${baseUrl}?${
    format ? `fm=${format}&` : ''
  }w=${w1x}&fit=scale ${w1x}w, ${baseUrl}?${
    format ? `fm=${format}&` : ''
  }w=${w2x}&fit=scale ${w2x}w`
}

const RenderImgSource = ({
  altText,
  classes,
  dataTestId,
  image,
  minWidth,
  El
}: RenderImgSourceProps) => {
  const isSvg = image.extension === 'svg'
  const imgSrc =
    image.optimizedImages && !isSvg ? image.optimizedImages.srcAvif : image.url
  const unoptimizedImgType = isSvg ? 'svg+xml' : image.extension
  const imgType = image.optimizedImages && !isSvg ? 'avif' : unoptimizedImgType
  const maxWidth = parseInt(minWidth) * 2
  if (El === 'img') {
    return (
      <>
        <source
          className={classes}
          srcSet={buildSrcSet(imgSrc, minWidth, 'avif')}
          sizes={`${
            minWidth ? `(max-width: ${maxWidth}px) 100vw, 50vw` : null
          }`}
          type={`image/${imgType}`}
        />
        <img
          alt={altText}
          className={classes}
          src={image.url}
          srcSet={buildSrcSet(image.url, minWidth)}
          sizes={`${
            minWidth ? `(max-width: ${maxWidth}px) 100vw, 50vw` : null
          }`}
          typeof={image.extension}
          data-testid={dataTestId}
        />
      </>
    )
  }
  return (
    <>
      <El
        className={classes}
        media={minWidth ? `(min-width: ${minWidth})` : null}
        srcSet={buildSrcSet(imgSrc, minWidth, 'avif')}
        type={`image/${imgType}`}
        sizes={`${minWidth ? `(max-width: ${maxWidth}px) 100vw, 50vw` : null}`}
        data-testid={dataTestId}
      />
      <El
        className={classes}
        media={minWidth ? `(min-width: ${minWidth})` : null}
        srcSet={buildSrcSet(image.url, minWidth)}
        sizes={`${minWidth ? `(max-width: ${maxWidth}px) 100vw, 50vw` : null}`}
        type={image.extension}
      />
    </>
  )
}

const ImageResolutions = ({
  imageAlt,
  images,
  mobileBleed = false,
  pictureClasses = '',
  classes,
  borderRadius = 'None'
}: ImageResolutionsProps) => {
  const imageCorners = Card.borderRadius(borderRadius)
  const imgClasses = classNames(
    classes,
    imageCorners,
    'overflow-hidden lazyload',
    {
      '-mx-4 md:mx-0': mobileBleed
    }
  )

  const imgResolutions = images?.imageResolutions && images.imageResolutions[0]

  if (!imgResolutions) return <></>

  const desktop =
    imgResolutions.desktop && imgResolutions.desktop.length > 0
      ? imgResolutions.desktop[0]
      : null
  const tablet =
    imgResolutions.tablet && imgResolutions.tablet.length > 0
      ? imgResolutions.tablet[0]
      : null
  const mobile =
    imgResolutions.mobile && imgResolutions.mobile.length > 0
      ? imgResolutions.mobile[0]
      : null
  const altText = imageAlt
    ? imageAlt
    : desktop?.description
    ? desktop.description
    : desktop?.title

  return (
    desktop && (
      <picture data-testid='mds-imageResolutions' className={pictureClasses}>
        <RenderImgSource
          dataTestId='mds-imageResolutions-source-desktop'
          El='source'
          image={desktop}
          minWidth='992px'
        />
        <RenderImgSource
          dataTestId='mds-imageResolutions-source-tablet'
          El='source'
          image={tablet || desktop}
          minWidth='768px'
        />
        <RenderImgSource
          altText={altText}
          dataTestId='mds-imageResolutions-img'
          image={mobile || desktop}
          El='img'
          classes={imgClasses}
        />
      </picture>
    )
  )
}

export default ImageResolutions
